import { Box } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { formatISO, isSameDay, isValid, parseISO } from 'date-fns';
import React from 'react';
import { DatePicker } from '../DatePicker';
/**
 * A composite date and time picker component that combines a DatePicker and a TimePicker.
 *
 * @component
 *
 * @param {DateTimePickerProps} props - Props for the DateTimePicker component.
 * @param {SxProps} [props.sx] - Optional styling via the `sx` prop.
 * @param {string} props.dateLabel - Label for the date field.
 * @param {string} props.timeLabel - Label for the time field.
 * @param {Date} [props.minDate] - Minimum selectable date (treated as a hint).
 * @param {Date} [props.maxDate] - Maximum selectable date (treated as a hint).
 * @param {'24h' | '12h'} [props.timeFormat] - Time format, defaults to `'24h'`.
 * @param {1 | 5 | 10 | 15 | 30 | 60} [props.timeMinuteStep] - Time step in minutes, default is 30.
 * @param {boolean} [props.required] - Whether both fields are required.
 * @param {boolean} [props.disabled] - Whether the fields are disabled.
 * @param {boolean} [props.readOnly] - Whether the fields are read-only.
 * @param {boolean} [props.fullWidth] - Whether the picker takes the full width.
 * @param {'inline' | 'column'} [props.display] - Display layout, `'inline'` (row) or `'column'`. Default is `'inline'`.
 * @param {'small' | 'medium'} [props.size] - Field size. Default is `'medium'`.
 * @param {boolean} [props.error] - Whether to indicate an error.
 * @param {number} [props.dateTabIndex] - Tab index for the date input.
 * @param {number} [props.timeTabIndex] - Tab index for the time input.
 * @param {DateTimePickerValue} props.value - The selected date and time value.
 * @param {(newValue: DateTimePickerValue) => void} props.onChange - Callback triggered on change.
 * @param {string} [props.'data-test-date'] - Test ID for the date field.
 * @param {string} [props.'data-test-time'] - Test ID for the time field.
 * @param {boolean} [props.autoFocus] - Whether the date field should autofocus on mount.
 * @param {string} [props.ariaDescribedBy] - ARIA description ID for accessibility.
 *
 * @returns {React.ReactElement} Rendered DateTimePicker component.
 */
export const DateTimePicker = (props) => {
    var _a, _b;
    const dateValue = getDateValue(props.value);
    const timeValue = getTimeValue(props.value);
    const handleDateChange = (newDateValue) => {
        props.onChange({
            date: makeDate(newDateValue, timeValue),
            dateValue: newDateValue,
            timeValue: timeValue,
        });
    };
    const handleTimeChange = (newTimeValue) => {
        props.onChange({
            date: makeDate(dateValue, newTimeValue),
            dateValue: dateValue,
            timeValue: newTimeValue,
        });
    };
    const rootSx = {
        display: 'flex',
        flexDirection: props.display === 'column' ? 'column' : 'row',
        gap: 2,
    };
    let childSx = undefined;
    if (props.fullWidth) {
        rootSx.width = '100%';
        childSx = { flexGrow: 1, flexBasis: 0 };
    }
    return (React.createElement(Box, { sx: Object.assign(Object.assign({}, rootSx), props.sx) },
        React.createElement(DatePicker, { sx: childSx, label: props.dateLabel, minDate: getMinDate(props.minDate), maxDate: getMaxDate(props.maxDate), required: props.required, disabled: props.disabled, readOnly: props.readOnly, fullWidth: props.fullWidth, size: props.size, error: props.error, tabIndex: props.dateTabIndex, value: dateValue, onChange: handleDateChange, "data-test": props['data-test-date'], autoFocus: (_a = props.autoFocus) !== null && _a !== void 0 ? _a : false, ariaDescribedBy: props.ariaDescribedBy }),
        React.createElement(TimePicker, { sx: childSx, label: props.timeLabel, minTime: getMinTime(dateValue, props.minDate), maxTime: getMaxTime(dateValue, props.maxDate), ampm: props.timeFormat === '12h', timeSteps: { minutes: (_b = props.timeMinuteStep) !== null && _b !== void 0 ? _b : 5 }, disabled: props.disabled, readOnly: props.readOnly, defaultValue: timeValue.time, onChange: (value) => {
                handleTimeChange({ time: value !== null && value !== void 0 ? value : undefined });
            }, slotProps: {
                textField: {
                    error: props.error,
                    size: props.size,
                    fullWidth: props.fullWidth,
                    required: props.required,
                    tabIndex: props.timeTabIndex,
                    'aria-describedby': props.ariaDescribedBy,
                },
            }, "data-test": props['data-test-time'] })));
};
function getDateValue(value) {
    if (value.dateValue) {
        return value.dateValue;
    }
    else if (value.date) {
        if (isValid(value.date)) {
            return { date: formatISO(value.date, { representation: 'date' }) };
        }
        else {
            return { invalidDate: value.date };
        }
    }
    else {
        return {};
    }
}
function getTimeValue(value) {
    if (value.timeValue) {
        return value.timeValue;
    }
    else if (value.date) {
        if (isValid(value.date)) {
            return { time: value.date };
        }
        else {
            return {};
        }
    }
    else {
        return {};
    }
}
function getMinDate(minDate) {
    if (minDate && isValid(minDate)) {
        return formatISO(minDate, { representation: 'date' });
    }
    else {
        return undefined;
    }
}
function getMaxDate(maxDate) {
    if (maxDate && isValid(maxDate)) {
        return formatISO(maxDate, { representation: 'date' });
    }
    else {
        return undefined;
    }
}
function getMinTime(dateValue, minDate) {
    if (minDate && isValid(minDate) && dateValue.date) {
        if (isSameDay(minDate, parseISO(dateValue.date))) {
            return new Date(minDate);
        }
        return undefined;
    }
    else {
        return undefined;
    }
}
function getMaxTime(dateValue, maxDate) {
    if (maxDate && isValid(maxDate) && dateValue.date) {
        if (isSameDay(maxDate, parseISO(dateValue.date))) {
            return new Date(maxDate);
        }
        return undefined;
    }
    else {
        return undefined;
    }
}
function makeDate(dateValue, timeValue) {
    if (dateValue.date) {
        const dateWithoutTime = parseISO(dateValue.date);
        if (isValid(dateWithoutTime) && timeValue.time) {
            return new Date(dateWithoutTime.getTime() + getTimeValueFromDate(timeValue.time));
        }
    }
    return undefined;
}
export function getTimeValueFromDate(date) {
    return date.getHours() * 60 * 60 * 1000 + date.getMinutes() * 60 * 1000 + date.getSeconds() * 1000 + date.getMilliseconds();
}
